<script lang="ts" setup>
import { CustomerTeamTaxDiscount } from '@/core/collections/contracts-api/customers/team-tax-discounts/team-tax-discount.interface';
import { TEAM_TAX_DISCOUNT_TYPE_ENDPOINTS } from '@/core/collections/contracts-api/team-tax-discount-types/team-tax-discount-type.endpoints';
import {
   TeamTaxDiscountType,
   TeamTaxDiscountTypeKey,
} from '@/core/collections/contracts-api/team-tax-discount-types/team-tax-discount-type.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import { ApiResource } from '@/core/models/ApiResource.interface';
import dayjs from 'dayjs';
import { PropType } from 'vue';

const { data: teamTaxDiscountTypes } = useFetch<ApiResource<Array<TeamTaxDiscountType>>>(TEAM_TAX_DISCOUNT_TYPE_ENDPOINTS.GET_ALL(), {
   ...cacheFirstConfig,
});

const data = defineModel<Partial<CustomerTeamTaxDiscount>>('modelValue', { required: true });

const props = defineProps({
   loading: { type: Boolean, default: false },
   errors: { type: Object, default: null },
   initialTaxDiscount: { type: Object as PropType<CustomerTeamTaxDiscount>, default: null },
});

//#region Dates
function setFrom(value: string) {
   const fromDate = dayjs(value).startOf('month').format('YYYY-MM-DD');
   if (data.value.till && dayjs(fromDate).isAfter(data.value.till)) {
      data.value.till = undefined;
   }
   data.value.from = fromDate;
}

function setTill(value?: string) {
   const tillDate = value ? dayjs(value).endOf('month').format('YYYY-MM-DD') : undefined;
   if (data.value.from && tillDate && dayjs(tillDate).isBefore(data.value.from)) {
      data.value.from = undefined;
   }
   data.value.till = tillDate;
}
//#endregion

//#region Team Tax Discount Types
const selectedTeamTaxDiscountType = computed(() =>
   teamTaxDiscountTypes.value?.data?.find((type) => type.id === data.value.team_tax_discount_type_id),
);

const showBisMonthlyPercentages = computed(() =>
   [TeamTaxDiscountTypeKey.BisShifts, TeamTaxDiscountTypeKey.ContiniousOperationBis].includes(
      selectedTeamTaxDiscountType.value?.key as TeamTaxDiscountTypeKey,
   ),
);

const groupedBisMonthlyPercentages = computed(() => {
   if (!data.value.bis_monthly_percentages) return {};

   return data.value.bis_monthly_percentages.reduce((acc: { [key: number]: typeof data.value.bis_monthly_percentages }, curr) => {
      const year = dayjs(curr.month_start_date).year();
      if (!acc[year]) acc[year] = [];
      acc[year].push(curr);
      return acc;
   }, {});
});

function autoSetShowMonthPercentages() {
   if (props.initialTaxDiscount) return;
   if (!showBisMonthlyPercentages.value || !data.value.from) {
      data.value.bis_monthly_percentages = undefined;
   } else {
      const startMonth = dayjs(data.value.from).startOf('month');
      const endMonth = data.value.till ? dayjs(data.value.till).endOf('month') : dayjs().endOf('month');

      const amountOfMonths = endMonth.diff(startMonth, 'month');

      const bisMonthlyPercentages = Array.from({ length: amountOfMonths + 1 }, (_, i) => ({
         month_start_date: startMonth.add(i, 'month').format('YYYY-MM-DD'),
      }));

      data.value.bis_monthly_percentages = bisMonthlyPercentages;
   }
}

function addMonth() {
   if (!data.value.from) return;
   const lastEntry = data.value.bis_monthly_percentages && data.value.bis_monthly_percentages.slice(-1)[0];
   const startDate = lastEntry ? dayjs(lastEntry.month_start_date).add(1, 'month') : dayjs(data.value.from).startOf('month');
   const newMonth = { month_start_date: startDate.format('YYYY-MM-DD') };
   if (data.value.bis_monthly_percentages) {
      data.value.bis_monthly_percentages.push(newMonth);
   } else {
      data.value.bis_monthly_percentages = [newMonth];
   }
}

watch([() => data.value.from, () => data.value.till, showBisMonthlyPercentages], autoSetShowMonthPercentages, { deep: true });
//#endregion

//#region Update Bis Monthly Percentage Value
async function updateBisMonthlyPercentageValue(bisMonthlyPercentage: any, value: string) {
   // Replace comma with dot, remove all non-numeric and non-dot characters, allow only one dot
   let sanitized = value.replace(',', '.').replace(/[^0-9.]/g, '');
   // Only keep the first dot, remove any additional dots
   const parts = sanitized.split('.');
   if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
   }

   // Enforce: first 2 digits cannot exceed 2, third digit must be decimal
   if (sanitized.length > 2 && !sanitized.includes('.')) {
      sanitized = sanitized.slice(0, 2) + '.' + sanitized.slice(2);
   }

   // Limit to 2 decimals if decimal part exists, remove extra decimals if more than 2
   if (sanitized.includes('.')) {
      const [intPart, decPart] = sanitized.split('.');
      let trimmedDecPart = decPart.slice(0, 2);
      sanitized = intPart + '.' + trimmedDecPart;
   }

   // Parse to float, if not a number set to undefined
   const num = parseFloat(sanitized);
   if (isNaN(num)) {
      bisMonthlyPercentage.value = undefined;
      return;
   }
   bisMonthlyPercentage.value = '';
   await nextTick();
   bisMonthlyPercentage.value = sanitized;
}
//#endregion
</script>

<template>
   <div :class="'tax-discount-form'">
      <div class="tax-discount-form__main">
         <Dropdown
            v-model="data.team_tax_discount_type_id"
            :label="$t('general.team-tax-discount-types')"
            :name="'team_tax_discount_type_id'"
            :options="teamTaxDiscountTypes?.data ?? []"
            :errors
            :disabled="loading || !!initialTaxDiscount?.id"
            :required="true"
            class="tax-discount-form__main__team-tax-discount-type"
         />

         <div class="tax-discount-form__main__dates">
            <NxtDatepicker
               :model-value="data.from"
               :label="$t('generic.dates.from')"
               :name="'from'"
               :errors
               :disabled="loading || !!initialTaxDiscount?.id"
               :required="true"
               :type="'month'"
               :max="data.till"
               @update:model-value="setFrom"
            />

            <NxtDatepicker
               :model-value="data.till"
               :label="$t('generic.dates.till')"
               :name="'till'"
               :errors
               :disabled="loading || !!initialTaxDiscount?.till"
               :required="false"
               :type="'month'"
               :min="data.from"
               @update:model-value="setTill"
            />
         </div>
      </div>

      <NxtSection :title="$t('customers.bis-monthly-percentages')" v-if="showBisMonthlyPercentages">
         <div class="tax-discount-form__bis-montly_percentages">
            <details v-for="(percentages, year) in groupedBisMonthlyPercentages" :key="year" :open="year == dayjs().year()">
               <summary>
                  <strong>{{ year }}</strong>
               </summary>
               <div
                  v-for="(bisMonthlyPercentage, index) in percentages"
                  :key="bisMonthlyPercentage.month_start_date"
                  class="tax-discount-form__bis-montly_percentages__month"
               >
                  <span>{{ $dayjs(bisMonthlyPercentage.month_start_date).format('MMMM') }}</span>
                  <NxtInput
                     :model-value="bisMonthlyPercentage.value"
                     :name="`bis-montly_percentages.${year}.${index}.value`"
                     :disabled="loading"
                     :errors="errors"
                     :placeholder="$t('generic.percentage')"
                     :icon-class="'fa-percent'"
                     :type="'text'"
                     @update:model-value="updateBisMonthlyPercentageValue(bisMonthlyPercentage, $event)"
                  />
               </div>
            </details>

            <NxtLink class="tax-discount-form__add-month" @on-click="addMonth" :disabled="loading || !data.from">
               <i class="fas fa-plus"></i>
               <span>
                  {{ $t('actions.add-type', { name: $t('generic.dates.month') }) }}
               </span>
            </NxtLink>
         </div>
      </NxtSection>
   </div>
</template>

<style lang="scss" scoped>
.tax-discount-form {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);

   min-width: toRem(600);
   min-height: toRem(200);
}

.tax-discount-form__main {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}

.tax-discount-form__main__team-tax-discount-type {
   width: toRem(300);
}

.tax-discount-form__main__dates {
   display: grid;
   grid-template-columns: toRem(200) toRem(200);
   gap: var(--nxt-gutter);
   align-items: center;
}

.tax-discount-form__bis-montly_percentages {
   padding: var(--nxt-gutter);
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-small);

   summary {
      padding: var(--nxt-gutter) 0;
   }
}

.tax-discount-form__bis-montly_percentages__month {
   display: grid;
   grid-template-columns: toRem(120) toRem(140);
   align-items: center;
   gap: var(--nxt-gutter);
   margin-bottom: toRem(5);
}

.tax-discount-form__add-month {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter-small);

   align-self: flex-start;
}
</style>
